import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { bookingService } from "@/lib/services";
import { PageShell } from "@/components/nav";
import { formatDate, formatUsh } from "@/lib/format";
import type { BookingStatus } from "@/types/api";

export const Route = createFileRoute("/_authenticated/bookings/")({
  component: BookingsList,
});

const statusStyles: Record<BookingStatus, string> = {
  pending: "bg-accent/40 text-secondary",
  processing: "bg-muted text-ink-muted",
  confirmed: "bg-primary/15 text-primary",
  checked_in: "bg-primary/15 text-primary",
  checked_out: "bg-muted text-ink-muted",
  completed: "bg-muted text-ink-muted",
  cancelled: "bg-destructive/10 text-destructive",
  rejected: "bg-destructive/10 text-destructive",
};

function BookingsList() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["guest-bookings", {}],
    queryFn: () => bookingService.guestBookings({}),
  });

  return (
    <PageShell>
      <div className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="text-3xl font-extrabold tracking-tight text-ink">My bookings</h1>
        <p className="mt-1 text-sm text-ink-muted">Stays you've requested or confirmed.</p>

        {isLoading && (
          <div className="mt-6 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-28 animate-pulse rounded-2xl border border-border brand-skeleton"
              />
            ))}
          </div>
        )}

        {isError && (
          <div className="mt-6 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {(error as Error).message}
          </div>
        )}

        {data && data.data.length === 0 && (
          <div className="mt-10 rounded-3xl border border-border bg-card p-10 text-center">
            <p className="text-lg font-bold text-ink">No bookings yet</p>
            <p className="mt-1 text-sm text-ink-muted">Find your next stay on the Discover page.</p>
            <Link
              to="/"
              className="mt-5 inline-block rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"
            >
              Browse listings
            </Link>
          </div>
        )}

        <ul className="mt-6 space-y-3">
          {data?.data.map((b) => (
            <li key={b.id}>
              <Link
                to="/bookings/$bookingId"
                params={{ bookingId: b.id }}
                className="block rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-card)]"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-ink-subtle">
                      {b.property?.name ?? "Stay"}
                    </p>
                    <p className="mt-1 text-base font-extrabold text-ink">
                      {formatDate(b.check_in_date)} → {formatDate(b.check_out_date)}
                    </p>
                    <p className="mt-1 text-xs text-ink-muted">
                      {b.guest_count} guest{b.guest_count === 1 ? "" : "s"} · {formatUsh(b.total)}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${statusStyles[b.status]}`}
                  >
                    {b.status.replace("_", " ")}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </PageShell>
  );
}
